import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}


class TLCCartReviews extends HTMLElement {
  connectedCallback() {
    this.index = 0;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.paused = this.reducedMotion.matches;
    this.visible = false;
    this.hovered = false;
    this.abort = new AbortController();
    const options = { signal: this.abort.signal };
    this.addEventListener('click', event => {
      const button = event.target.closest('[data-review-action]');
      if (!button) return;
      if (button.dataset.reviewAction === 'pause') {
        this.paused = !this.paused;
        this.updatePause();
      } else {
        this.show(this.index + (button.dataset.reviewAction === 'next' ? 1 : -1));
      }
    }, options);
    this.addEventListener('mouseenter', () => { this.hovered = true; }, options);
    this.addEventListener('mouseleave', () => { this.hovered = false; }, options);
    this.reducedMotion.addEventListener('change', event => {
      if (event.matches) { this.paused = true; this.updatePause(); }
    }, options);
    this.observer = new IntersectionObserver(entries => {
      this.visible = entries.some(entry => entry.isIntersecting);
    }, { threshold: 0.5 });
    this.observer.observe(this);
    this.timer = setInterval(() => {
      if (!this.paused && !this.hovered && this.visible && !this.contains(document.activeElement) && !document.hidden && this.closest('dialog')?.open) this.show(this.index + 1);
    }, 6000);
    this.show(0);
    this.updatePause();
  }
  disconnectedCallback() {
    clearInterval(this.timer);
    this.abort?.abort();
    this.observer?.disconnect();
  }
  updatePause() {
    const button = this.querySelector('[data-review-action="pause"]');
    if (!button) return;
    button.textContent = this.paused ? 'Play' : 'Pause';
    button.setAttribute('aria-label', this.paused ? 'Play review slideshow' : 'Pause review slideshow');
  }
  show(index) {
    const slides = [...this.querySelectorAll('[data-review-slide]')];
    this.index = (index + slides.length) % slides.length;
    this.querySelector('[data-review-track]').style.transform = 'translateX(-' + (this.index * 100) + '%)';
    slides.forEach((slide,i) => slide.setAttribute('aria-hidden', String(i !== this.index)));
    const counter = this.querySelector('[data-review-count]');
    if (counter) counter.textContent = (this.index + 1) + ' / ' + slides.length;
  }
}
if (!customElements.get('tlc-cart-reviews')) customElements.define('tlc-cart-reviews', TLCCartReviews);
